// ============================================================================
// Derived envelope status for the sender dashboard.
//
// The stored `status` column has five values and cannot distinguish the two
// states a sender most wants to see on a list: "someone has signed but we're
// still waiting on the rest" and "this quietly expired and nobody noticed".
// Both are derived from the recipients and the link lifetime rather than
// stored, because storing them would mean a background job keeping them
// honest, and a stale status is worse than a computed one.
// ============================================================================

export type DerivedStatus =
  | 'Draft'
  | 'Sent'
  | 'Viewed'
  | 'Partially signed'
  | 'Completed'
  | 'Expired'
  | 'Declined'
  | 'Voided';

export interface StatusInput {
  status: string;
  created_at: string;
  expires_in_days: number | null;
}

export interface StatusRecipient {
  role: string;
  status: string;
}

export const DEFAULT_LINK_DAYS = 14;

/** Has the signing window closed on an envelope that never completed? */
export function isExpired(env: StatusInput, now = new Date()): boolean {
  if (!['Sent', 'In_Progress'].includes(env.status)) return false;
  const days = env.expires_in_days ?? DEFAULT_LINK_DAYS;
  const deadline = new Date(env.created_at).getTime() + days * 86_400_000;
  return now.getTime() > deadline;
}

export function deriveStatus(
  env: StatusInput,
  recipients: StatusRecipient[],
  now = new Date(),
): DerivedStatus {
  if (env.status === 'Voided') return 'Voided';
  if (env.status === 'Completed') return 'Completed';
  if (env.status === 'Draft') return 'Draft';

  const signers = recipients.filter((r) => r.role !== 'CC');
  if (signers.some((r) => r.status === 'Declined')) return 'Declined';
  if (isExpired(env, now)) return 'Expired';

  const signed = signers.filter((r) => r.status === 'Signed').length;
  if (signed > 0 && signed < signers.length) return 'Partially signed';
  if (signers.some((r) => r.status === 'Viewed')) return 'Viewed';
  return 'Sent';
}

/** Days left on the signing window, or null when it does not apply. */
export function daysRemaining(
  env: StatusInput,
  now = new Date(),
): number | null {
  if (!['Sent', 'In_Progress'].includes(env.status)) return null;
  const days = env.expires_in_days ?? DEFAULT_LINK_DAYS;
  const deadline = new Date(env.created_at).getTime() + days * 86_400_000;
  return Math.ceil((deadline - now.getTime()) / 86_400_000);
}

export const DERIVED_STATUS_STYLE: Record<DerivedStatus, string> = {
  Draft: 'bg-tcs-surface text-tcs-text-secondary',
  Sent: 'bg-st-info-bg text-st-info-fg',
  Viewed: 'bg-st-info-bg text-st-info-fg-strong',
  'Partially signed': 'bg-st-warn-bg text-st-warn-fg-strong',
  Completed: 'bg-st-ok-bg text-st-ok-fg',
  Expired: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Declined: 'bg-st-danger-bg text-st-danger-fg',
  Voided: 'bg-tcs-surface text-tcs-text-muted',
};
